import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SofteamboardProvider, useSofteamboardStore } from './store/softeamboardStore';
import { UsersProvider } from './contexts/UsersContext';
// ✅ FIXED: Import components from correct paths
import SofteamBoard from './components/recruiting/SofteamBoard';
import NeedsList from './components/recruiting/NeedsList';
import Candidates from './pages/Candidates'; // ✅ ADD: Import Candidates page
import LoginPage from './components/auth/LoginPage';
import UserManagement from './components/admin/UserManagement';
import FlowsAdmin from './pages/FlowsAdmin';
import WorkflowRequestsList from './pages/WorkflowRequestsList';
import WorkflowRequestDetail from './pages/WorkflowRequestDetail';
import FinishedNeeds from './pages/FinishedNeeds';
import MeetingMinutes from './pages/MeetingMinutes';
import JobConnectPublicationsPage from './features/jobconnect/JobConnectPublicationsPage';
import ErrorBoundary from './components/ErrorBoundary';
import EditNeedModal from './components/recruiting/EditNeedModal';
import { validateWorkflowRequirements } from './utils/workflowValidation';
import './App.css';

// Configuration
// Use relative path for production (nginx proxy) or env var if set
const SETUP_URL = process.env.REACT_APP_SETUP_URL || '/api/v1/setups/';

// Setup check component
const SetupCheck = ({ children }) => {
  const [isSetupComplete, setIsSetupComplete] = useState(null);

  const checkSetupStatus = useCallback(async () => {
    try {
      console.log('🔧 Checking setup status at:', SETUP_URL);
      const response = await fetch(SETUP_URL);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔧 Setup status response:', data);
        setIsSetupComplete(data.is_setup_complete);
      } else {
        console.warn('🔧 Setup check failed with status:', response.status);
        // If setup endpoint fails, assume setup is complete to avoid blocking the app
        setIsSetupComplete(true);
      }
    } catch (error) {
      console.error('🔧 Error checking setup status:', error);
      // If setup endpoint fails, assume setup is complete to avoid blocking the app
      setIsSetupComplete(true);
    }
  }, []);

  useEffect(() => {
    checkSetupStatus();
  }, []);

  if (isSetupComplete === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking setup status...</p>
        </div>
      </div>
    );
  }

  return children;
};

// Protected route component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'Admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You need administrator privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (user?.role === 'TranscriptOnly' && window.location.pathname !== '/meeting-minutes') {
    return <Navigate to="/meeting-minutes" replace />;
  }

  return children;
};

// Main app content
const AppContent = () => {
  const { isAuthenticated, user } = useAuth();
  const { postAnalysisModal, setPostAnalysisModal } = useSofteamboardStore();

  const handlePostAnalysisSuccess = useCallback((updatedNeed) => {
    // Check if all required fields are now filled
    const { missingFields } = validateWorkflowRequirements(updatedNeed);
    
    if (missingFields.length === 0) {
      // All fields filled - can now trigger workflows
      setPostAnalysisModal({ open: false, need: null, missingFields: [] });
      
      // Optionally show success message
      // showToast('Required fields completed! Workflows can now be triggered.', 'success'); // Assuming showToast is available
    } else {
      // Still missing fields - keep modal open
      setPostAnalysisModal(prev => ({
        ...prev,
        need: updatedNeed,
        missingFields
      }));
    }
  }, [setPostAnalysisModal]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <main>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to={user?.role === 'TranscriptOnly' ? '/meeting-minutes' : '/softeamboard'} replace /> : <LoginPage />}
        />
        
        {/* Protected routes */}
        <Route path="/softeamboard/*" element={
          <ProtectedRoute>
            <SofteamBoard />
          </ProtectedRoute>
        } />
        
        <Route path="/softeamboard/needs" element={
          <ProtectedRoute>
            {user?.role === 'Recruiter' ? <Navigate to="/softeamboard" replace /> : <NeedsList />}
          </ProtectedRoute>
        } />
        
        <Route path="/softeamboard/finished" element={
          <ProtectedRoute>
            {user?.role === 'Recruiter' ? <Navigate to="/softeamboard" replace /> : <FinishedNeeds />}
          </ProtectedRoute>
        } />

        {/* ✅ ADD: Candidates route - This was missing! */}
        <Route path="/candidates" element={
          <ProtectedRoute>
            {user?.role === 'Recruiter' ? <Navigate to="/softeamboard" replace /> : <Candidates />}
          </ProtectedRoute>
        } />

        <Route path="/jobconnect-publications" element={
          <ProtectedRoute>
            {user?.role === 'Recruiter' ? <Navigate to="/softeamboard" replace /> : <JobConnectPublicationsPage />}
          </ProtectedRoute>
        } />

        {/* Meeting minutes */}
        <Route path="/meeting-minutes" element={
          <ProtectedRoute>
            <MeetingMinutes />
          </ProtectedRoute>
        } />

        {/* Admin routes */}
        <Route path="/admin/users" element={
          <ProtectedRoute adminOnly>
            <UserManagement />
          </ProtectedRoute>
        } />

        <Route path="/admin/flows" element={
          <ProtectedRoute adminOnly>
            <FlowsAdmin />
          </ProtectedRoute>
        } />

        <Route path="/admin/workflows" element={
          <ProtectedRoute>
            {user?.role === 'Recruiter' ? <Navigate to="/softeamboard" replace /> : <WorkflowRequestsList />}
          </ProtectedRoute>
        } />

        <Route path="/admin/workflows/:id" element={
          <ProtectedRoute>
            {user?.role === 'Recruiter' ? <Navigate to="/softeamboard" replace /> : <WorkflowRequestDetail />}
          </ProtectedRoute>
        } />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to={user?.role === 'TranscriptOnly' ? '/meeting-minutes' : '/softeamboard'} replace />} />
        <Route path="/*" element={<Navigate to={user?.role === 'TranscriptOnly' ? '/meeting-minutes' : '/softeamboard'} replace />} />
      </Routes>
      </main>
      {/* Post-analysis modal */}
      <EditNeedModal
        need={postAnalysisModal.need}
        open={postAnalysisModal.open}
        onClose={() => setPostAnalysisModal({ open: false, need: null, missingFields: [] })}
        onSuccess={handlePostAnalysisSuccess}
        mode="post-analysis"
        missingFields={postAnalysisModal.missingFields}
        requiredForWorkflows={postAnalysisModal.requiredForWorkflows}
      />
    </Router>
  );
};

// Root app component
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <UsersProvider>
          <SetupCheck>
            <SofteamboardProvider>
              <AppContent />
            </SofteamboardProvider>
          </SetupCheck>
        </UsersProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
